import clsx from "clsx";
import { useAtom } from "jotai";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router";

import { Copy, Download } from "@/assets/icons/ds";
import { Button, SubmitButton } from "@/components/Interface/Button/Button";
import { CardModule } from "@/components/Layout/BaseLayouts/CardModule/CardModule";
import { useAlert } from "@/hooks/useAlert";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { useConfirmTwoFactorAuthenticationBackupCodes } from "@/services/auth";
import { AlertTypes, twoFactorBackupCodesAtom } from "@/utils/atoms";

import {
  copyCodesToClipboard,
  downloadCodes as _downloadCodes,
  stepDescriptionClassName,
  stepHeadingClassName,
  stepSubheadingClassName,
} from "./authenticator.utils";
import { NameConfirmationField } from "./NameConfirmationField";

export const AuthenticatorBackupCodeConfirm = ({
  successTarget,
}: {
  successTarget: string;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [backupCodes] = useAtom(twoFactorBackupCodesAtom);

  type NameConfirmationForm = {
    name: string;
  };
  const formMethods = useForm<NameConfirmationForm>({
    defaultValues: {
      name: "",
    },
    mode: "onSubmit",
  });
  const name = useWatch({ control: formMethods.control, name: "name" });

  const { mutateAsync: confirmBackupCodes } =
    useConfirmTwoFactorAuthenticationBackupCodes();

  const handleFormSubmit = formMethods.handleSubmit(() => {
    confirmBackupCodes()
      .then(() => {
        showToast({
          text: t("portal:authenticator.backupCodes.success"),
          type: AlertTypes.success,
        });
        navigate(successTarget);
      })
      .catch(() => {
        formMethods.setError("name", {
          type: "manual",
          message: t("portal:authenticator.backupCodes.error"),
        });
      });
  });

  const showToast = useAlert();
  const copyCodes = () =>
    copyCodesToClipboard(backupCodes)
      .then(() => {
        showToast({
          text: t("portal:authenticator.backupCodes.copySuccess"),
          type: AlertTypes.success,
        });
      })
      .catch(() => {
        showToast({
          text: t("portal:authenticator.backupCodes.copyError"),
          type: AlertTypes.error,
        });
      });

  const downloadCodes = () => {
    _downloadCodes(backupCodes);
    showToast({
      text: t("portal:authenticator.backupCodes.downloadSuccess"),
      type: AlertTypes.success,
    });
  };

  if (!backupCodes.length) {
    return <Navigate to={portalRoutes.user.security.initialAppSetup} />;
  }

  return (
    <form onSubmit={handleFormSubmit}>
      <CardModule
        stickyHeader
        title={t("portal:authenticator.backupCodes.title")}
        actions={
          <SubmitButton
            fullWidth={false}
            size="sm"
            label={t("Common.label.continue")}
            disabled={!name.trim()}
          />
        }
      >
        <p className={clsx("whitespace-pre-line", stepDescriptionClassName)}>
          {t("portal:authenticator.backupCodes.description")}
        </p>

        <div className="flex flex-col gap-2">
          <div className="flex flex-col rounded-md bg-surface-tertiary px-5 py-4 font-semibold leading-md text-foreground-primary">
            <span className="text-body-xs">
              {t("portal:authenticator.backupCodes.uniqueRecoveryCodes")}
            </span>
            <ul className="flex flex-col gap-3 text-body-base">
              {backupCodes.map((code) => (
                <li key={code}>{code}</li>
              ))}
            </ul>
          </div>
          <div className="flex gap-2">
            <Button
              variant="tertiary"
              size="sm"
              type="button"
              onClick={copyCodes}
              icon={<Copy className="h-4 w-4" />}
            >
              {t("portal:authenticator.backupCodes.copy")}
            </Button>
            <Button
              variant="tertiary"
              size="sm"
              type="button"
              onClick={downloadCodes}
              icon={<Download className="h-4 w-4" />}
            >
              {t("portal:authenticator.backupCodes.download")}
            </Button>
          </div>
        </div>

        <h3 className={stepHeadingClassName}>
          {t("portal:authenticator.backupCodes.proceedWithSteps")}
        </h3>
        <div className="flex flex-col gap-2">
          <h4 className={stepSubheadingClassName}>
            {t("portal:authenticator.backupCodes.steps.1.title")}
          </h4>
          <p className={stepDescriptionClassName}>
            {t("portal:authenticator.backupCodes.steps.1.description")}
          </p>
        </div>
        <NameConfirmationField
          register={formMethods.register}
          errors={formMethods.formState.errors}
        />
      </CardModule>
    </form>
  );
};
